import Column from '@app/components/layout/Column'
import {
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
  ReactNode,
  useEffect,
} from 'react'
import { useTranslations } from '@i18n/useLanguage'
import { Button } from '@app/components/core/button/Button'
import { Style } from 'inlines'

export type ContentCollapsibleProps = {
  children: ReactNode
  truncateAfter?: number
}

const lineClampStyle = (truncateAfter: number): Style => ({
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: truncateAfter,
})

export const ContentCollapsible = ({
  children,
  truncateAfter,
}: ContentCollapsibleProps) => {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!truncateAfter) {
    return <div data-testid="contentCollapsible">{children}</div>
  }

  if (mounted) {
    return (
      <ContentCollapsibleClient truncateAfter={truncateAfter}>
        {children}
      </ContentCollapsibleClient>
    )
  }

  return (
    <div style={lineClampStyle(truncateAfter)} data-testid="contentCollapsible">
      {children}
    </div>
  )
}

const ContentCollapsibleClient = ({
  children,
  truncateAfter,
}: ContentCollapsibleProps) => {
  const translations = useTranslations().app.common
  const [hasMore, setHasMore] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const textRef = useRef<HTMLDivElement>(null)

  const checkClamp = useCallback(() => {
    const element = textRef.current
    if (element) {
      const hasOverflow = element.scrollHeight > element.clientHeight + 1
      setHasMore(hasOverflow)
    }
  }, [])

  useLayoutEffect(() => {
    checkClamp()
    window.addEventListener('resize', checkClamp)
    return () => window.removeEventListener('resize', checkClamp)
  }, [checkClamp, children])

  return (
    <Column>
      <div
        ref={textRef}
        style={!isExpanded ? lineClampStyle(truncateAfter) : {}}
        data-testid="contentCollapsible"
      >
        {children}
      </div>
      {hasMore && (
        <Button
          variant="link"
          label={translations.readMore}
          onClick={() => {
            setIsExpanded(true)
            setHasMore(false)
          }}
        />
      )}
    </Column>
  )
}
